import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SpacerService, SpacerDetailDto, SpacerBatchDto, SpacerTestsDto, SpacerLabSheetDto, SpacerMeasuredMaterialsDto, SpacerBatchMaterialDto, SubmitMeasuredMaterialsRequestDto, SlurryStatuses } from '../../../../services/spacer.service';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { PrintService } from '../../../../shared/print/print-dialog.component';
import { MeasureBatchMaterialComponent, MeasureBatchMaterialData } from '../measure-dry-material/measure-dry-material.component';
import { MudMeasureDialogComponent, MudMeasureDialogData } from '../mud-measure-dialog/mud-measure-dialog.component';
import { FoamMeasureDialogComponent, FoamMeasureDialogData } from '../foam-measure-dialog/foam-measure-dialog.component';
import { FoamCupMeasureDialogComponent, FoamCupMeasureDialogData } from '../foam-cup-measure-dialog/foam-cup-measure-dialog.component';

interface BatchTest {
  testInputMasterId: number;
  name: string;
  type: 'compatibility' | 'qaqc' | 'ssst' | 'hpht';
  isSelected: boolean;
  isLocked: boolean;
}

enum SpacerCompatibilityRatioType {
  Mud = 1,
  Cement = 2,
  Spacer = 3
}

const DEFAULT_MEASUREMENT_TOLERANCE = 0.5;

@Component({
  selector: 'app-batch-detail',
  imports: [CommonModule, FormsModule, SpinnerComponent],
  templateUrl: './batch-detail.component.html',
  styleUrls: ['./batch-detail.component.css'],
})
export class BatchDetailComponent implements OnInit, OnChanges {
  // Input parameters from parent component
  @Input() batchId: number | null = null;
  @Input() spacerId: number | null = null;
  @Input() projectId: number | null = null;
  @Input() projectName: string = '';
  @Input() spacerName: string = '';
  @Input() spacerStatus: SlurryStatuses | null = null;
  @Input() projectStatus: string = '';

  @ViewChild('labSheetContent')
  labSheetContent!: ElementRef<HTMLElement>;

  labSheetHtml = '';

  // Batch data
  batch: SpacerBatchDto | null = null;
  measuredMaterials: SpacerMeasuredMaterialsDto | null = null;
  spacerDetail: SpacerDetailDto | null = null;
  availableTests: BatchTest[] = [];
  isLoading: boolean = true;
  foamerApplicants: SpacerBatchMaterialDto[] = [];
  // Mix Batch section

  mixVolume = 800;

  private previousMixVolume = 800;
  private updateLabVolume(mixAsFoam: boolean): void {

    if (mixAsFoam) {

      this.previousMixVolume = this.mixVolume;

      this.mixVolume = 1170;

    } else {

      this.mixVolume =
        this.previousMixVolume > 0
          ? this.previousMixVolume
          : (this.spacerDetail?.targetLabVolume ?? 800);

    }

  }


  isToleranceSpecified = false;
  measurementTolerance: number | null = null;

  mixAsFoam = false;
  foamDensity = 0;

  excessFoamPercent = 10;

  selectedFoamerMaterialIds: number[] = [];

  private readonly LB_PER_GAL_IN_G_PER_ML = 0.119826;

  get totalWeight(): number {

    return Number(
      (
        (this.mixVolume ?? 0) *
        (this.foamDensity ?? 0) *
        this.LB_PER_GAL_IN_G_PER_ML
      ).toFixed(3)
    );

  }

  get showBlendTotal(): boolean {
    return this.spacerDetail?.isBlendTest ?? false;
  }

  get blendLabWeight(): number {

    return this.dryMaterials
      .filter(x => !x.afterBlend)
      .reduce(
        (sum, x) => sum + (x.labWeight ?? 0),
        0
      );
  }

  get blendDryMaterials(): SpacerBatchMaterialDto[] {
    return this.dryMaterials.filter(x => !x.afterBlend);
  }

  get afterBlendDryMaterials(): SpacerBatchMaterialDto[] {
    return this.dryMaterials.filter(x => x.afterBlend);
  }


  get resultDensity(): number | null {

    const foam = this.measuredMaterials?.foam;

    if (!foam?.cupWeight || !foam?.cupVolume) {
      return null;
    }

    return Number(
      (foam.cupWeight / foam.cupVolume)
        .toFixed(3)
    );
  }

  get isMaterialsLocked(): boolean {
    return this.measuredMaterials?.isMaterialsLocked ?? false;
  }

  get dryMaterials(): SpacerBatchMaterialDto[] {
    return this.measuredMaterials?.materials.filter(
      x => !x.isFoamer && x.isDry
    ) ?? [];
  }

  get liquidMaterials(): SpacerBatchMaterialDto[] {
    return this.measuredMaterials?.materials.filter(
      x => !x.isFoamer && !x.isDry
    ) ?? [];
  }

  get foamerMaterials(): SpacerBatchMaterialDto[] {
    return this.measuredMaterials?.materials.filter(
      x => x.isFoamer
    ) ?? [];
  }

  get mixAsFoamEnabled(): boolean {

  const hasFoamers =
    this.foamerApplicants?.length > 0;

  const anyMaterialMeasured =
    this.measuredMaterials?.materials.some(
      m =>
        m.measuredWeight != null ||
        !!m.measuredContainer
    ) ?? false;

  const blendMeasured =
    this.batch?.measuredBlendWeight != null ||
    !!this.batch?.capturedBlendCisProcontainerId;

  return !this.isMaterialsLocked
      && hasFoamers
      && !anyMaterialMeasured
      && !blendMeasured
      && this.canUpdateBatch;
}

  get canSubmitMix(): boolean {

  if (!this.measuredMaterials) {
    return false;
  }

  let materialsMeasured: boolean;

  if (this.showBlendTotal) {

    // Only validate individually measured materials
    materialsMeasured =
      this.measuredMaterials.materials
        .filter(x => x.afterBlend || !x.isDry)
        .every(x =>
          !!x.measuredContainer &&
          x.measuredWeight != null
        );

    const blendMeasured =
      this.batch?.measuredBlendWeight != null &&
      !!this.batch?.capturedBlendCisProcontainerId;

    if (!materialsMeasured || !blendMeasured) {
      return false;
    }

  } else {

    materialsMeasured =
      this.measuredMaterials.materials.every(x =>
        !!x.measuredContainer &&
        x.measuredWeight != null
      );

    if (!materialsMeasured) {
      return false;
    }
  }

  if (this.mixAsFoam) {

    const foam = this.measuredMaterials.foam;

    return !!foam &&
      foam.measuredWeight != null &&
      foam.cupWeight != null &&
      foam.cupVolume != null;
  }

  return true;
}


  constructor(
    private spacerService: SpacerService, private printService: PrintService, private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadBatchData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when batchId changes
    if (changes['batchId'] && !changes['batchId'].firstChange) {
      this.loadBatchData();
    }
  }

  private convertRatioType(type: number | null | undefined): string {
    return type != null ? (SpacerCompatibilityRatioType[type] ?? 'Unknown') : 'Unknown';
  }

  loadBatchData(): void {
    if (!this.batchId || !this.spacerId) {
      console.error('Missing batchId or spacerId');
      return;
    }

    this.isLoading = true;

    // Load batch details
    // Load batch details
    this.spacerService.getBatchById(this.batchId).subscribe({
      next: (batch) => {

        this.batch = batch;
        this.loadMeasuredMaterials();

        this.spacerService.getById(this.spacerId!).subscribe(spacer => {

          this.spacerDetail = spacer;

          this.mixVolume =
            this.batch?.batchLabVolume
            ?? spacer.targetLabVolume
            ?? 800;


          this.foamerApplicants =
            spacer.materials
              ?.filter(m => m.sectionType === 'LiquidAdditive')
              .map(m => {

                const measuredMaterial =
                  this.measuredMaterials?.materials.find(
                    x => x.materialName === m.materialName
                  );

                return {
                  ...measuredMaterial,
                  slurryMaterialId:
                    measuredMaterial?.slurryMaterialId ?? m.materialId,
                  materialName:
                    measuredMaterial?.materialName ?? m.materialName,
                  isFoamer:
                    measuredMaterial?.isFoamer ?? false
                } as SpacerBatchMaterialDto;
              })
            ?? [];

        });

        this.measurementTolerance =
          this.batch.measurementTolerance ??
          DEFAULT_MEASUREMENT_TOLERANCE;

        this.isToleranceSpecified =
          this.batch.measurementTolerance != null;

        this.foamDensity =
          this.batch.foamDensity ?? 0;

        this.excessFoamPercent =
          this.batch.excessFoamPercent ?? 10;

        //this.loadAvailableTests();
      },
      error: (error) => {
        console.error('Error loading batch:', error);
        alert('Failed to load batch details');
        this.isLoading = false;
      }
    });
  }
  private loadMeasuredMaterials(): void {

    if (!this.batchId) {
      return;
    }

    this.spacerService
      .getMeasuredMaterials(this.batchId)
      .subscribe({
        next: result => {

          this.measuredMaterials = result;

          this.selectedFoamerMaterialIds =
            result.materials
              .filter(x => x.isFoamer)
              .map(x => x.slurryMaterialId);

          this.mixAsFoam =
            this.selectedFoamerMaterialIds.length > 0;


          // Initialize foam object if mixAsFoam is true and foam is null
          if (!this.measuredMaterials.foam) {
            this.measuredMaterials.foam = {
              measuredWeight: undefined,
              weightComments: '',
              cupWeight: undefined,
              cupVolume: undefined,
              resultDensity: undefined
            };
          }

          this.loadAvailableTests();
        },
        error: error => {
          console.error(
            'Error loading measured materials',
            error
          );
        }
      });
  }

  loadAvailableTests(): void {
    if (!this.spacerId || !this.batch) return;

    this.spacerService.getTestInputs(this.spacerId).subscribe({
      next: (testInputs) => {
        this.processTestInputs(testInputs);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading test inputs:', error);
        this.isLoading = false;
      }
    });
  }

  submitMix(): void {

    if (!this.batchId ||
      !this.measuredMaterials) {
      return;
    }

    // const request: SubmitMeasuredMaterialsRequestDto =
    // {
    //   materials:
    //     this.measuredMaterials.materials,

    //   foam:
    //     this.mixAsFoam
    //       ? this.measuredMaterials.foam
    //       : undefined
    // };
    const request: SubmitMeasuredMaterialsRequestDto =
    {
      materials: this.measuredMaterials.materials,

      foam:
        this.mixAsFoam
          ? this.measuredMaterials.foam
          : undefined,

      measuredBlendWeight:
        this.batch?.measuredBlendWeight,

      capturedBlendContainer:
        this.batch?.capturedBlendCisProcontainerId,

      blendWeightComments:
        this.batch?.blendWeightComments,

      blendContainerComments:
        this.batch?.blendContainerComments,

      measuredMudContainer:
        this.batch?.measuredMudContainer,

      mudContainerComments:
        this.batch?.mudContainerComments,

        replacePreferredBlendContainer:
          this.batch?.replacePreferredBlendContainer,
 
      replacePreferredMudContainer:
          this.batch?.replacePreferredMudContainer
    };


    this.spacerService
      .submitMeasuredMaterials(
        this.batchId,
        request
      )
      .subscribe({

        next: result => {

          this.measuredMaterials =
            result;

          alert(
            'Mix submitted successfully.'
          );

          this.loadMeasuredMaterials();
        },

        error: error => {

          console.error(
            'Submit mix failed',
            error
          );

          const message =
            error?.error?.detail ||
            error?.message ||
            'Failed to submit mix';

          alert(message);
        }
      });
  }

  private processTestInputs(testInputs: SpacerTestsDto): void {
    this.availableTests = [];

    // Add Compatibility test
    if (testInputs.spacerCompatibility) {
      const isSelected = this.batch?.tests.some(
        t => t.testInputMasterId === testInputs.spacerCompatibility!.testInputMasterId
      ) || false;

      this.availableTests.push({
        testInputMasterId: testInputs.spacerCompatibility.testInputMasterId,
        name: 'Spacer Compatibility',
        type: 'compatibility',
        isSelected: isSelected,
        isLocked: this.isMaterialsLocked && isSelected
      });
    }

    // Add QAQC test
    if (testInputs.spacerQaQc) {
      const isSelected = this.batch?.tests.some(
        t => t.testInputMasterId === testInputs.spacerQaQc!.testInputMasterId
      ) || false;

      this.availableTests.push({
        testInputMasterId: testInputs.spacerQaQc.testInputMasterId,
        name: 'Spacer QAQC',
        type: 'qaqc',
        isSelected: isSelected,
        isLocked: this.isMaterialsLocked && isSelected
      });
    }

    // Add SSST test
    if (testInputs.spacerSsst) {
      const isSelected = this.batch?.tests.some(
        t => t.testInputMasterId === testInputs.spacerSsst!.testInputMasterId
      ) || false;

      this.availableTests.push({
        testInputMasterId: testInputs.spacerSsst.testInputMasterId,
        name: 'Spacer SSST',
        type: 'ssst',
        isSelected: isSelected,
        isLocked: this.isMaterialsLocked && isSelected
      });
    }

    // Add HPHT tests
    let fannCount = 0;
    let channCount = 0;

    (testInputs.spacerHpHtRheologyInputs || []).forEach((hpht) => {
      let currentNumber: number;
      let testName: string;

      switch (hpht.hpHtRheologyTypeId) {
        case 1: // Fann77
          currentNumber = ++fannCount;
          testName = `Spacer HPHT Rheology (Fann 77) # ${currentNumber}`;
          break;
        case 2: // Chann7600
          currentNumber = ++channCount;
          testName = `Spacer HPHT Rheology (Chann 7600) # ${currentNumber}`;
          break;
        case 3: // Chann7550
          currentNumber = ++channCount;
          testName = `Spacer HPHT Rheology (Chann 7550) # ${currentNumber}`;
          break;
        default:
          currentNumber = ++channCount;
          testName = `Spacer HPHT Rheology # ${currentNumber}`;
          break;
      }

      const isSelected = this.batch?.tests.some(
        t => t.testInputMasterId === hpht.testInputMasterId
      ) || false;

      this.availableTests.push({
        testInputMasterId: hpht.testInputMasterId,
        name: testName,
        type: 'hpht',
        isSelected: isSelected,
        isLocked: this.isMaterialsLocked && isSelected
      });
    });
  }

  roundTo2(value: number | null | undefined): number | null {
  if (value == null) {
    return null;
  }

  return Number(value.toFixed(2));
}

  // Update batch tests
  updateBatch(): void {

    if (!this.batchId || !this.spacerId || !this.batch) {
      alert('Cannot update batch: missing required data');
      return;
    }

    const selectedTestIds = this.availableTests
      .filter(t => t.isSelected)
      .map(t => t.testInputMasterId);


    const request = {
      testInputMasterIds: selectedTestIds,

      batchLabVolume: this.mixVolume,

      measurementTolerance:
        this.isToleranceSpecified
          ? (this.measurementTolerance ?? undefined)
          : undefined,

      foamDensity:
        this.mixAsFoam
          ? this.foamDensity
          : undefined,

      excessFoamPercent:
        this.mixAsFoam
          ? this.excessFoamPercent
          : undefined,

      selectedFoamerMaterialIds:
    this.selectedFoamerMaterialIds,

  // Blend fields
  measuredBlendWeight:
    this.batch.measuredBlendWeight,

  capturedBlendContainer:
    this.batch.capturedBlendCisProcontainerId,

  preferredBlendContainer:
    this.batch.preferredBlendContainer,

  replacePreferredBlendContainer:
    this.batch.replacePreferredBlendContainer,

  blendContainerComments:
    this.batch.blendContainerComments,

  blendWeightComments:
    this.batch.blendWeightComments,

  // Mud fields
  measuredMudContainer:
    this.batch.measuredMudContainer,

  preferredMudContainer:
    this.batch.preferredMudContainer,

  replacePreferredMudContainer:
    this.batch.replacePreferredMudContainer,

  mudContainerComments:
    this.batch.mudContainerComments
};

    this.spacerService
      .updateBatchTests(this.batchId, request)
      .subscribe({
        next: () => {
          this.loadBatchData();
        },
        error: (error) => {
          console.error('Error updating batch:', error);

          const errorMessage =
            error?.error?.detail ||
            error?.message ||
            'Failed to update batch';

          alert(`Error: ${errorMessage}`);
        }
      });
  }

  onMixAsFoamChanged(): void {

    this.updateLabVolume(this.mixAsFoam);

  }

  toggleFoamer(material: SpacerBatchMaterialDto): void {


    const exists =
      this.selectedFoamerMaterialIds.includes(
        material.slurryMaterialId
      );

    const measuredMaterial =
      this.measuredMaterials?.materials.find(
        x => x.slurryMaterialId === material.slurryMaterialId
      );

    if (exists) {

      this.selectedFoamerMaterialIds =
        this.selectedFoamerMaterialIds.filter(
          x => x !== material.slurryMaterialId
        );

      material.isFoamer = false;

      if (measuredMaterial) {
        measuredMaterial.isFoamer = false;
      }

    } else {

      this.selectedFoamerMaterialIds.push(
        material.slurryMaterialId
      );

      material.isFoamer = true;

      if (measuredMaterial) {
        measuredMaterial.isFoamer = true;
      }
    }

  }




  redoDryMix(): void {

    this.dryMaterials.forEach(material => {
      material.measuredWeight = null as any;
      material.measuredContainer = '';
      material.weightComments = '';
      material.containerComments = '';
    });

    // Unlock materials to allow re-measuring
    if (this.measuredMaterials) {
      this.measuredMaterials.isMaterialsLocked = false;
    }

  }


  redoLiquidMix(): void {

    this.liquidMaterials.forEach(material => {
      material.measuredWeight = null as any;
      material.measuredContainer = '';
      material.weightComments = '';
      material.containerComments = '';
    });

    // Unlock materials to allow re-measuring
    if (this.measuredMaterials) {
      this.measuredMaterials.isMaterialsLocked = false;
    }

  }

  redoMudMix(): void {

    if (this.batch) {
      this.batch.measuredMudContainer = '';
      this.batch.mudContainerComments = '';
    }

    // Unlock materials to allow re-measuring
    if (this.measuredMaterials) {
      this.measuredMaterials.isMaterialsLocked = false;
    }

  }

  redoFoamMix(): void {

    const foam = this.measuredMaterials?.foam;

    if (!foam) {
      return;
    }

    foam.measuredWeight = null as any;
    foam.cupWeight = null as any;
    foam.cupVolume = null as any;
    foam.weightComments = '';

    // Unlock materials to allow re-measuring
    if (this.measuredMaterials) {
      this.measuredMaterials.isMaterialsLocked = false;
    }

  }

  measureMaterialWithScanner(material: SpacerBatchMaterialDto): void {
    if (!this.batchId) return;

    const tolerance =
      this.isToleranceSpecified && this.measurementTolerance !== null
        ? this.measurementTolerance
        : DEFAULT_MEASUREMENT_TOLERANCE;

    const data: MeasureBatchMaterialData = {
      batchId: this.batchId,
      material,
      tolerance,
    };

    const dialogRef = this.dialog.open(MeasureBatchMaterialComponent, {
      data,
      width: '660px',
      disableClose: true,
    });

    // dialogRef.afterClosed().subscribe((updated: SpacerBatchMaterialDto | null) => {
    //   if (updated) {
    //     this.loadMeasuredMaterials();
    //   }
    // });
    dialogRef.afterClosed().subscribe((updated: SpacerBatchMaterialDto | null) => {
  if (updated) {
    material.measuredWeight = updated.measuredWeight;
    material.measuredContainer = updated.measuredContainer;
    material.weightComments = updated.weightComments;
    material.containerComments = updated.containerComments;
    material.replacePreferredContainer =
          updated.replacePreferredContainer;
  }
});

  }

  measureBlend(): void {
    if (!this.batch) {
      return;
    }

    const dialogData: MeasureBatchMaterialData = {
      batchId: this.batchId!,
      tolerance: this.measurementTolerance ?? 0.5,
      isBlend: true,
      calculatedWeight: this.blendLabWeight,
      preferredContainer: this.batch.preferredBlendContainer,
      measuredBlendWeight: this.batch.measuredBlendWeight,
      measuredBlendContainer: this.batch.capturedBlendCisProcontainerId,
      blendWeightComments: this.batch.blendWeightComments,
      blendContainerComments: this.batch.blendContainerComments,
      replacePreferredBlendContainer: this.batch.replacePreferredBlendContainer
    };

    const dialogRef = this.dialog.open(MeasureBatchMaterialComponent, {
      width: '600px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.batch) {
        this.batch.measuredBlendWeight = result.measuredBlendWeight;
        this.batch.capturedBlendCisProcontainerId = result.measuredBlendContainer;
        this.batch.blendWeightComments = result.blendWeightComments;
        this.batch.blendContainerComments = result.blendContainerComments;
        this.batch.replacePreferredBlendContainer = result.replacePreferredBlendContainer;
      }
    });
  }

  measureMud(): void {
    if (!this.batch) {
      return;
    }

    const dialogData: MudMeasureDialogData = {
      preferredMudContainer: this.batch.preferredMudContainer,
      measuredMudContainer: this.batch.measuredMudContainer,
      mudContainerComments: this.batch.mudContainerComments
    };

    const dialogRef = this.dialog.open(MudMeasureDialogComponent, {
      width: '600px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
  if (result && this.batch) {
    this.batch.measuredMudContainer = result.measuredMudContainer;
    this.batch.mudContainerComments = result.mudContainerComments;

    this.batch.replacePreferredMudContainer =
      result.replacePreferredContainer;
  }
});
  }

  measureFoam(): void {


    if (!this.measuredMaterials) {
      return;
    }

    if (!this.measuredMaterials.foam) {

      this.measuredMaterials.foam = {
        measuredWeight: undefined,
        weightComments: '',
        cupWeight: undefined,
        cupVolume: undefined,
        resultDensity: undefined
      };
    }

    const dialogData: FoamMeasureDialogData = {
      calculatedFoamWeight: this.totalWeight,
      measuredWeight: this.measuredMaterials.foam.measuredWeight,
      weightComments: this.measuredMaterials.foam.weightComments,
      measurementTolerance: this.measurementTolerance ?? undefined
    };


    const dialogRef = this.dialog.open(FoamMeasureDialogComponent, {
      width: '600px',
      data: dialogData
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result && this.measuredMaterials?.foam) {
        this.measuredMaterials.foam.measuredWeight = result.measuredWeight;
        this.measuredMaterials.foam.weightComments = result.weightComments;
      }
    });
  }

  measureFoamCup(): void {

    if (!this.measuredMaterials) {
      return;
    }

    if (!this.measuredMaterials.foam) {

      this.measuredMaterials.foam = {
        measuredWeight: undefined,
        weightComments: '',
        cupWeight: undefined,
        cupVolume: undefined,
        resultDensity: undefined
      };
    }

    const dialogData: FoamCupMeasureDialogData = {
      cupWeight: this.measuredMaterials.foam.cupWeight,
      cupVolume: this.measuredMaterials.foam.cupVolume
    };


    const dialogRef = this.dialog.open(FoamCupMeasureDialogComponent, {
      width: '600px',
      data: dialogData
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result && this.measuredMaterials?.foam) {
        this.measuredMaterials.foam.cupWeight = result.cupWeight;
        this.measuredMaterials.foam.cupVolume = result.cupVolume;
      }
    });
  }


  get hasSelectedTests(): boolean {
    return this.availableTests.some(t => t.isSelected);
  }

  isMaterialMeasured(material: SpacerBatchMaterialDto): boolean {
    return (
      material.measuredWeight !== undefined &&
      material.measuredWeight !== null &&
      !!material.measuredContainer &&
      material.measuredContainer.trim() !== ''
    );
  }

  isBlendMeasured(): boolean {
  return (
    this.batch?.measuredBlendWeight !== undefined &&
    this.batch?.measuredBlendWeight !== null &&
    !!this.batch?.capturedBlendCisProcontainerId &&
    this.batch.capturedBlendCisProcontainerId.trim() !== ''
  );
}

  isMudMeasured(): boolean {
    return (
      !!this.batch?.measuredMudContainer &&
      this.batch.measuredMudContainer.trim() !== ''
    );
  }

  isFoamWeightMeasured(): boolean {
    return (
      this.measuredMaterials?.foam?.measuredWeight !== undefined &&
      this.measuredMaterials?.foam?.measuredWeight !== null
    );
  }

  isFoamCupMeasured(): boolean {
    return (
      this.measuredMaterials?.foam?.cupWeight !== undefined &&
      this.measuredMaterials?.foam?.cupWeight !== null &&
      this.measuredMaterials?.foam?.cupVolume !== undefined &&
      this.measuredMaterials?.foam?.cupVolume !== null
    );
  }

  printBatchTests(): void {

    if (!this.spacerId || !this.batch) {
      return;
    }

    this.spacerService.getLabSheetData(this.spacerId)
      .subscribe({
        next: (labSheetData) => {

          const selectedTests =
            this.availableTests.filter(x => x.isSelected);

          this.labSheetHtml =
            this.buildLabSheetHtml(
              selectedTests,
              labSheetData
            );
          this.labSheetHtml =
            this.buildLabSheetHtml(
              selectedTests,
              labSheetData
            );

          const contentEl =
            this.labSheetContent.nativeElement;

          contentEl.innerHTML =
            this.labSheetHtml;

          setTimeout(() => {

            this.printService.print(contentEl, {
              windowWidth: window.screen.availWidth,
              windowHeight: window.screen.availHeight,
              bodyPadding: '8mm',
              pageSize: 'A4 portrait',
              pageMargin: '5mm',
              decorators: [
                {
                  selector: '.lab-table',
                  style: `
      width: 100% !important;
      border-collapse: collapse !important;
      table-layout: fixed !important;
      display: table !important;
      margin-top:4px !important;
      margin-bottom:8px !important;
  `
                },
                {
                  selector: '.lab-table th',
                  style: `
      border: 1px solid #000 !important;
      padding: 8px 4px !important;
      text-align:center !important;
      font-weight:bold !important;
      display: table-cell !important;
  `
                },
                {
                  selector: '.lab-table td',
                  style: `
      border: 1px solid #000 !important;
      padding: 6px 4px !important;
      display: table-cell !important;
  `
                },
                {
                  selector: '.lab-table tr',
                  style: `
      display: table-row !important;
  `
                }, {
                  selector: 'table',
                  style: `
    border-collapse: collapse !important;
    width: 100% !important;
    table-layout: fixed !important;
    display: table !important;
    font-family: GothamNarrowBook !important;
    font-size: 10px !important;
  `
                },
                {
                  selector: 'tr',
                  style: `
    display: table-row !important;
  `
                },
                {
                  selector: 'td, th',
                  style: `
    display: table-cell !important;
    padding: 4px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  `
                },
                {
                  selector: '.materials-table',
                  style: `
      width:100% !important;
      border:none !important;
      border-collapse:collapse !important;
      margin:0 !important;
      padding:0!important;
  `
                },
                {
                  selector: '.materials-table th',
                  style: `
      border:none !important;
      background:#DBDCDD !important;
      padding:2px 4px 2px 12px !important;
      font-weight:bold !important;
  `
                },
                {
                  selector: '.materials-table td',
                  style: `
      border:none !important;
      border-bottom:1px solid #DBDCDD !important;
      background:#FAAB18 !important;
      padding:2px 4px 2px 12px !important;
  `
                },
                {
                  selector: '.materials-title',
                  style: `
      background:#DBDCDD !important;
      text-align:left !important;
      font-weight:bold !important;
      border:none !important;
      padding:4px 0 4px 2px !important;
      margin:0 !important;
  `
                },
                {
                  selector: '.materials-heading',
                  style: `
      background:#DBDCDD !important;
      font-weight:bold !important;
  `
                },

                {
                  selector: '.header-table',
                  style: `
      border: 1px solid black !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
  `
                },
                {
                  selector: '.header-table td',
                  style: `
      border: none !important;
      padding: 5px !important;
  `
                },
                {
                  selector: '.header-table, .header-table tr, .header-table td',
                  style: `
      background: #ffffff !important;
      background-color: #ffffff !important;
  `
                },
                {
                  selector: '.header-value',
                  style: `
      text-decoration: underline !important;
      color:#000000 !important;
  `
                },
                {
                  selector: '.qaqc-notes-label',
                  style: `
      width:80px !important;
      min-width:80px !important;
      max-width:80px !important;

      text-align:left !important;
      vertical-align:middle !important;

      font-weight:normal !important;

      border-right:none !important;
      padding-left:8px !important;
  `
                },
                {
                  selector: '.qaqc-notes-value',
                  style: `
      border-left:none !important;
      vertical-align:top !important;
  `
                },
                {
                  selector: '.qaqc-notes-highlight',
                  style: `
      background:#B2CC34 !important;
      width:65% !important;
      padding:4px !important;
      white-space:pre-wrap !important;
      display:block !important;

      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
  `
                },
                {
                  selector: '.mixability-table, .mixability-table td',
                  style: `
      border-bottom:1px solid #000;
      padding:4px 0;
      margin:0 0 12px 0;
  `
                },
                {
                  selector: '.notes-section',
                  style: `
      border-bottom:1px solid #000;
      padding:4px 0;
      margin:0 0 12px 0;
  `
                },
                {
                  selector: '.liquid-materials-separator',
                  style: `
      border-bottom:1px solid #000;
      margin:0 0 12px 0;
  `
                },
                {
                  selector: '.materials-top-line',
                  style: `
      border-top:1px solid #000;
  `
                },
                {
                  selector: 'hr',
                  style: `
      border: 0 !important;
      border-top: 1px solid #000 !important;
      margin: 12px 0 !important;
      height: 0 !important;
  `
                },
                {
                  selector: '.hpht-table',
                  style: `
    width:auto !important;
table-layout:auto !important;
  `
                },
                {
                  selector: '.report-print-table thead',
                  style: `
    display: table-header-group !important;
  `
                },
                {
                  selector: '.report-print-table',
                  style: `
    width:100% !important;
    border-collapse:collapse !important;
  `
                },

              ],
              fallbackBodyClass: 'lab-sheet-printing'
            });

          }, 100);
        }
      });
  }

  private buildLabSheetHtml(testsToInclude: BatchTest[], labSheetData: SpacerLabSheetDto): string {


    const sentToLab =
      labSheetData.sentToLab
        ? new Date(
          labSheetData.sentToLab
        ).toLocaleDateString('en-US')
        : '';
    let html = `
<table class="report-print-table" style="width:100%;">
  <thead>
    <tr>
      <td>
`;
    html += `
<table
class="header-table"
style="
width:100%;
margin-bottom:8px;
border:1px solid #000;
border-collapse:separate;
border-spacing:0;
">
`;
    html += '<tr>';
    html += `<td style="width:33%; padding:5px; "><strong>Project:</strong> <span class="header-value">${this.escapeHtml(labSheetData.projectName)}</span></td>`;
    html += `<td style="width:33%; padding:5px; "><strong>Slurry:</strong> <span class="header-value">${this.escapeHtml(labSheetData.slurryName)}</span></td>`;
    html += `<td style="width:33%; padding:5px;"><strong>Project By:</strong> <span class="header-value">${this.escapeHtml(labSheetData.projectBy)}</span></td>`;
    html += '</tr>';
    html += '<tr>';
    html += `<td style="padding: 5px;"><strong>Rig Name:</strong> <span class="header-value">${this.escapeHtml(labSheetData.rigName)}</span></td>`;
    html += `
<td colspan="2" style="padding:5px;">
<strong>Well Name:</strong> <span class="header-value">${this.escapeHtml(labSheetData.wellName)}</span>
</td>`;

    html += '</tr>';
    html += '<tr>';

    html += `
<td colspan="3" style="padding:5px;">
<strong>Job Type:</strong><span class="header-value"> ${this.escapeHtml(labSheetData.jobType)}</span>
${labSheetData.pipeSize
        ? `&nbsp;&nbsp;&nbsp;<strong>Pipe Size:</strong> <span class="header-value">${this.escapeHtml(labSheetData.pipeSize)}</span>`
        : ''
      }
</td>`;

    html += '</tr>';
    html += '</table>';
    html += `
      </td>
    </tr>
  </thead>

  <tbody>
    <tr>
      <td>
`;

    html += '<hr>';
    html += `<div>
Density:
<strong>${labSheetData.density.toFixed(2)}ppg</strong>
`;

    if (labSheetData.mudWeight != null) {
      html += `
  &nbsp;&nbsp;&nbsp;
  Mud Weight:
  <strong>${Number(labSheetData.mudWeight).toFixed(2)}ppg</strong>
  `;
    }

    html += `
&nbsp;&nbsp;&nbsp;
Sent To Lab:
<strong>${sentToLab}</strong>
</div>`;
    html += '<hr>';
    html += `
<div
style="
border-top:1px solid #000;
border-bottom:1px solid #000;
padding:0;
margin-bottom:10px;
">
`;
    html += `
<div class="materials-top-line"></div>`;
    html += '<table class="materials-table">';
    html += `
<colgroup>
  <col style="width:22%">
  <col style="width:10%">
  <col style="width:12%">
  <col style="width:16%">
  <col style="width:28%">
  <col style="width:12%">
</colgroup>`;
    html += '<thead>';

    html += `
<tr>
  <th colspan="6" class="materials-title">
    Dry Materials
  </th>
</tr>`;
    html += '<tr>';
    html += '<th style="width:22%; text-align:center;">Material Name</th>';
    html += '<th style="width:10%; text-align:center;">Percent (%)</th>';
    html += '<th style="width:12%; text-align:center;">Weight(lb)</th>';
    html += '<th style="width:16%; text-align:center;">Preferred Container</th>';
    html += '<th style="width:28%; text-align:center;">Container Location</th>';
    html += '<th style="width:12%; text-align:center;">Lab Weight</th>';
    html += '</tr>';
    html += '</thead>';

    html += '<tbody>';
    if (labSheetData.dryMaterials.length === 0) {
      html += '<tr><td colspan="6" style=" padding: 3px; text-align: center;">No dry materials</td></tr>';
    } else {
      labSheetData.dryMaterials.forEach(material => {
        html += '<tr>';
        html += `<td style="padding: 3px;">${this.escapeHtml(material.materialName)}</td>`;
        html += `<td style="padding: 3px; text-align: right;">
    ${material.percent ?? '-'}
</td>`;
        html += `
<td style=" padding: 3px; text-align: right;">
${(material.weight || 0).toFixed(3)}
</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.preferredContainer || '')}</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.containerLocation || '')}</td>`;
        html += `<td style="padding: 3px; text-align: right;">${(material.labWeight || 0).toFixed(3)}</td>`;
        html += '</tr>';
      });
    }
    html += '</tbody>';
    html += '</table>';
    html += '<table class="materials-table">';
    html += `
<colgroup>
  <col style="width:28%">
  <col style="width:12%">
  <col style="width:18%">
  <col style="width:28%">
  <col style="width:14%">
</colgroup>`;
    html += '<thead>';

    html += `
<tr>
  <th colspan="5" class="materials-title">
    Liquid Materials
  </th>
</tr>`;
    html += '<tr>';
    html += '<th style="width:28%; text-align:center;">Material Name</th>';
    html += '<th style="width:12%; text-align:center;">Gal/Sk</th>';
    html += '<th style="width:18%; text-align:center;">Preferred Container</th>';
    html += '<th style="width:28%; text-align:center;">Container Location</th>';
    html += '<th style="width:14%; text-align:center;">Lab Weight</th>';
    html += '</tr>';
    html += '</thead>';
    html += '<tbody>';


    if (labSheetData.liquidMaterials.length === 0) {
      html += '<tr><td colspan="5" style=" padding: 3px; text-align: center;">No liquid materials</td></tr>';
    } else {
      labSheetData.liquidMaterials.forEach(material => {
        html += '<tr>';
        html += `<td style="padding: 3px;">${this.escapeHtml(material.materialName)}</td>`;
        html += `
<td style="padding: 3px; text-align: right;">
${material.galPerSack != null ? `${Number(material.galPerSack).toFixed(3)} gal/sk` : ''}
</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.preferredContainer || '')}</td>`;
        html += `<td style="padding: 3px;">${this.escapeHtml(material.containerLocation || '')}</td>`;
        html += `<td style="padding: 3px; text-align: right;">${(material.labWeight || 0).toFixed(3)}</td>`;
        html += '</tr>';
      });
    }
    html += '</tbody>';
    html += '</table>';
    html += `
<div class="liquid-materials-separator"></div>
`;

    if (labSheetData.mixabilityScore == null) {
      html += `
  <table class="mixability-table">
      <tr>
          <td style="padding-right:20px;">
              <strong>Mixability</strong>
          </td>

          <td style="padding-right:20px;">
              Score:
          </td>

          <td style="padding-right:20px;">
              Time To Wet Solids (sec):
          </td>

          <td>
              Notes:
          </td>
      </tr>
  </table>
  `;
    }
    if (labSheetData.notes) {
      html += `
  <div class="notes-section">
      <strong>Notes:</strong>
      ${this.escapeHtml(labSheetData.notes)}
  </div>
  `;
    }
    html += this.buildTestsSectionsNew(testsToInclude, labSheetData);

    html += `
      </td>
    </tr>
  </tbody>
</table>
`;

    return html;
  }

  private buildTestsSectionsNew(testsToInclude: BatchTest[], labSheetData: SpacerLabSheetDto): string {
    let sections = '';

    testsToInclude.forEach(test => {
      switch (test.type) {
        case 'compatibility':
          sections += this.buildCompatibilitySectionNew(labSheetData);

          break;
        case 'qaqc':
          sections += this.buildQaqcSectionNew(labSheetData);
          break;
        case 'ssst':
          sections += this.buildSsstSectionNew(labSheetData);
          break;
        case 'hpht':
          sections += this.buildHphtSectionNew(test, labSheetData);
          break;
      }
    });
    return sections;
  }

  private buildCompatibilitySectionNew(labSheetData: SpacerLabSheetDto): string {
    const compat = labSheetData.spacerCompatibility;
    if (!compat) return '';

    // return '';
    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
  <div style="
  margin-top:6px;
  margin-bottom:4px;
  "><strong>
  Spacer Compatibility
  </strong></div>`; html += '<div style="margin-top: 5px;">';
    html += `
<div style="display:flex; gap:20px;">
  <span>
    Temperature:
    <strong>${compat.temperature || 'N/A'}°F</strong>
  </span>

  <span>
    Compatibility Project:
    <strong>${compat.compatibilityProjectName || 'N/A'}</strong>
  </span>

  <span>
    Compatibility Slurry:
    <strong>${compat.compatibilitySlurryUfid || 'N/A'}</strong>
  </span>
</div>
`;
    html += `
  <table class="lab-table">
    <tr>
      <td class="qaqc-notes-label">
        Notes:
      </td>
  
      <td class="qaqc-notes-value">
        ${
          compat.notes
        ? `
            <div class="qaqc-notes-highlight">
              ${this.escapeHtml(compat.notes).replace(/\r?\n/g, '<br>')}
            </div>
            `
        : '&nbsp;'
      }
      </td>
    </tr>
  </table>
  `;
    //     if (compat.ratios?.length) {

    //       html += `
    //   <table class="lab-table">

    //   <thead>
    //       <tr>
    //           <th style="border:1px solid #000;padding:4px;">
    //               Ratio
    //           </th>
    //       </tr>
    //   </thead>
    //   <tbody>
    //   `;

    //       compat.ratios.forEach(ratio => {
    //         html += `
    //       <tr>
    //           <td style="border:1px solid #000;padding:4px;">
    //               ${ratio.percentage}% ${this.convertRatioType(ratio.ratioType1)}
    // :
    // ${100 - ratio.percentage}% ${this.convertRatioType(ratio.ratioType2)}
    //           </td>
    //       </tr>`;
    //       });

    //       html += `
    //   </tbody>
    //   </table>`;
    //     }
    const totalWeight = compat.totalWeight ?? 0;

    const ratioRows = [
      { cement: false, spacer: 0 },
      { cement: false, spacer: 5 },
      { cement: false, spacer: 25 },
      { cement: false, spacer: 50 },
      { cement: false, spacer: 75 },
      { cement: false, spacer: 95 },
      { cement: false, spacer: 100 },

      { cement: true, spacer: 95 },
      { cement: true, spacer: 75 },
      { cement: true, spacer: 50 },
      { cement: true, spacer: 25 },
      { cement: true, spacer: 5 },
      { cement: true, spacer: 0 }
    ];

    html += `
<table class="lab-table">
  <thead>
    <tr>
      <th style="width:240px">% By Volume</th>
      <th>600</th>
      <th>300</th>
      <th>200</th>
      <th>100</th>
      <th>60</th>
      <th>30</th>
      <th>20</th>
      <th>10</th>
      <th>6</th>
      <th>3</th>
    </tr>
  </thead>
  <tbody>
`;

    ratioRows.forEach(r => {

      const spacerWeight =
        totalWeight * (r.spacer / 100);

      let text = '';

      if (!r.cement) {

        if (r.spacer === 0) {
          text = '100% Mud';
        } else if (r.spacer === 100) {
          text = `100% Spacer (${totalWeight.toFixed(2)}g)`;
        } else {
          text =
            `${100 - r.spacer}% Mud ${r.spacer}% Spacer (${spacerWeight.toFixed(2)}g)`;
        }

      } else {

        if (r.spacer === 0) {
          text = '100% Cement';
        } else {
          text =
            `${100 - r.spacer}% Cement ${r.spacer}% Spacer (${spacerWeight.toFixed(2)}g)`;
        }
      }

      html += `
    <tr>
      <td>${text}</td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
    </tr>
  `;
    });

    html += `
  </tbody>
</table>
`;
    html += `
<table class="lab-table">
  <tr>
    <td>Started By</td>
    <td></td>

    <td>Stopped By</td>
    <td></td>
  </tr>

  <tr>
    <td>Comments</td>
    <td colspan="3"></td>
    
  </tr>
  <tr>
    <td>Batch#</td>
    <td colspan="3"></td>
  </tr>
</table>
`;
    if (compat.mudContainerDeleted) {
      html += `
      <div style="color:red;font-weight:bold;margin-top:5px;">
        Warning: Mud container has been deleted
      </div>
    `;
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  private buildQaqcSectionNew(labSheetData: SpacerLabSheetDto): string {
    const qaqc = labSheetData.spacerQaQc;
    if (!qaqc) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
  <div style="
  margin-top:6px;
  margin-bottom:4px;
  "><strong>
  Spacer QAQC
  </strong></div>`; html += '<div style="margin-top: 5px;">';
    html += `
  <div>
    Temperature:
    <strong>${qaqc.temperature || 'N/A'}°F</strong>
  </div>
  `;

    if (qaqc.notes) {
      html += `
    <table class="lab-table">
      <tr>
        <td class="qaqc-notes-label">
          Notes:
        </td>
  
        <td class="qaqc-notes-value">
    ${
      qaqc.notes
          ? `
        <div class="qaqc-notes-highlight">
          ${this.escapeHtml(qaqc.notes)
            .replace(/\r?\n/g, '<br>')}
        </div>
        `
          : '&nbsp;'
        }
  </td>
      </tr>
    </table>
    `;
    }
    if (qaqc.readingTitles?.length) {

      qaqc.readingTitles.forEach(reading => {

        html += `
        <div style="margin-top:8px;font-weight:bold;">
          ${this.escapeHtml(reading.title)}
          ${reading.temperature != null
            ? ` @ ${reading.temperature}°F`
            : ''}
        </div>
  
        <table class="lab-table">
          <tr>
            <th>Lab/RPM</th>
            <th>600</th>
            <th>300</th>
            <th>200</th>
            <th>100</th>
            <th>60</th>
            <th>30</th>
            <th>20</th>
            <th>10</th>
            <th>6</th>
            <th>3</th>
            <th>Pv:cP</th>
            <th>Ty: lbf/100ft²</th>
          </tr>
  
          <tr>
            <td>CVX</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </table>
      `;
      });

      html += `
      <table class="lab-table">
        <tr>
          <td>Started By</td>
          <td></td>
          <td>Stopped By</td>
          <td></td>
        </tr>
  
        <tr>
          <td>Comments</td>
          <td colspan="3"></td>
        </tr>
        <tr>
    <td>Batch#</td>
    <td colspan="3"></td>
  </tr>
      </table>
    `;
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  private buildSsstSectionNew(labSheetData: SpacerLabSheetDto): string {
    const ssst = labSheetData.spacerSsst;
    if (!ssst) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
  <div style="
  margin-top:6px;
  margin-bottom:4px;
  "><strong>Spacer SSST</strong>
  </div>`;
    html += `<div style="margin-top: 5px;">Temperature: <strong>${ssst.temperature || 'N/A'}°F</strong></div>`;
    html += `
  <table class="lab-table">
  <tr>
  <td colspan="8" style="border:1px solid #000;padding:4px;">
  <strong>Notes:</strong>
  ${this.escapeHtml(ssst.notes || '')}
  </td>
  </tr>
  </table>
  `;
    html += `
  <table class="lab-table">
  
  <tr>
    <td style="width:120px;">Started By</td>
    <td style="width:60px;"></td>
  
    <td style="width:80px;">Stopped By</td>
    <td style="width:60px;"></td>
  </tr>
  
  <tr>
    <td>Conductivity Of Pure Spacer</td>
    <td></td>
  
    <td>Starting Mud Volume</td>
    <td></td>
  
    <td>Water Dispersion</td>
    <td>Yes / No</td>
  
    <td>Rod Cleaning Test</td>
    <td>Pass / Fail</td>
  </tr>
  
  <tr>
    <td>Comments</td>
    <td colspan="7"></td>
  </tr>
  
  </table>
  `;
    html += `
  <table class="lab-table">
  <thead>
  <tr>
  <th>Spacer Added (ml)</th>
  <th>Spacer (%)</th>
  <th>Conductivity</th>
  <th>Baseline</th>
  </tr>
  </thead>
  <tbody>
  `;
    for (let i = 0; i < 18; i++) {
      html += `
    <tr>
      <td>&nbsp;</td>
      <td>&nbsp;</td>
      <td>&nbsp;</td>
      <td>&nbsp;</td>
    </tr>
    `;
    }
    html += `
  </tbody>
  </table>
  `;
  html += `
<table class="lab-table">
  <tr>
    <td style="width:120px;">Batch#</td>
    <td></td>
  </tr>
</table>
`;
    html += '</div>';
    return html;
  }

  private buildHphtSectionNew(test: BatchTest, labSheetData: SpacerLabSheetDto): string {
    const hphtTests = labSheetData.spacerHpHtRheology || [];
    const hphtIndex = this.availableTests.filter(t => t.type === 'hpht').indexOf(test);
    const hpht = hphtTests[hphtIndex];

    if (!hpht) return '';

    let html = '<div style="margin-top: 15px; page-break-inside: avoid;">';
    html += `
  <div style="
  margin-top:6px;
  margin-bottom:4px;
  ">
  <strong>Spacer HPHT Rheology Test</strong>
  </div>`;
    html += `<div style="margin-top: 5px;">Equipment: <strong>${hpht.equipment || 'N/A'}</strong></div>`;

    html += `
  <table class="lab-table">
    <tr>
      <td class="qaqc-notes-label">
        Notes:
      </td>
  
      <td class="qaqc-notes-value">
    ${hpht.notes
        ? `
          <div class="qaqc-notes-highlight">
            ${this.escapeHtml(hpht.notes).replace(/\r?\n/g, '<br>')}
          </div>
        `
        : '&nbsp;'
      }
  </td>
    </tr>
  </table>
  `;

    if (hpht.readings && hpht.readings.length > 0) {

      html += `
    <table class="lab-table hpht-table">
  
      <colgroup>
        <col style="width:200px">
        <col style="width:55px">
        <col style="width:60px">
        <col style="width:70px">
  
        <col style="width:40px">
        <col style="width:50px">
        <col style="width:60px">
        <col style="width:44px">
  
        <col style="width:55px">
        <col style="width:55px">
  
        <col style="width:40px">
        <col style="width:40px">
  
        <col style="width:55px">
        <col style="width:55px">
  
        <col style="width:60px">
        <col style="width:60px">
      </colgroup>
  
      <thead>
        <tr>
          <th>Reading Temp (°F)</th>
          <th>Reading Pressure (psi)</th>
          <th>Reading Time</th>
          <th></th>
  
          <th>600</th>
          <th>300</th>
          <th>200</th>
          <th>100</th>
          <th>60</th>
          <th>30</th>
          <th>20</th>
          <th>10</th>
          <th>6</th>
          <th>3</th>
          <th>10 sec</th>
          <th>10 min</th>
        </tr>
      </thead>
  
      <tbody>
    `;

      hpht.readings.forEach(reading => {

        html += `
        <tr>
          <td rowspan="2">${reading.temperature ?? ''}</td>
          <td rowspan="2">${reading.pressure ?? ''}</td>
          <td rowspan="2">${reading.time ?? ''}</td>
  
          <td>RampUp</td>
  
          <td></td>
  <td></td>
  
  <td colspan="2"></td>
  
  <td colspan="2"></td>
  
  <td></td>
  
  <td colspan="2"></td>
  
  <td colspan="3"></td>
        </tr>
  
        <tr>
          <td>RampDown</td>
  
          <td></td>
  <td></td>
  
  <td colspan="2"></td>
  
  <td colspan="2"></td>
  
  <td></td>
  
  <td colspan="2"></td>
  
  <td colspan="3"></td>
        </tr>
      `;
      });

      html += `
        <tr>
          <td>Started By</td>
          <td></td>
  
          <td>Stopped By</td>
          <td></td>
  
          <td>Test Date</td>
          <td></td>
          <td></td>
  
          <td>Equip. No.</td>
  
          <td colspan="8"></td>
        </tr>
  
        <tr>
          <td>Comments</td>
          <td colspan="15"></td>
        </tr>
   <tr>
    <td>Batch#</td>
    <td colspan="3"></td>
  </tr>
      </tbody>
    </table>
    `;
    } else {
      html += '<div style="margin-top: 8px; font-style: italic;">No readings available</div>';
    }

    html += '</div>';

    return html;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Add permission getters
  get canUpdateBatch(): boolean {
    if (this.projectStatus === 'Completed') return false;
    if (!this.spacerStatus) return false;
    return this.spacerStatus === SlurryStatuses.Draft ||
      this.spacerStatus === SlurryStatuses.DraftAgain ||
      this.spacerStatus === SlurryStatuses.OngoinginLab 
  }

  get canMeasureMaterials(): boolean {
    if (this.projectStatus === 'Completed') return false;
    if (!this.spacerStatus) return false;
    return this.spacerStatus === SlurryStatuses.OngoinginLab ||
      this.spacerStatus === SlurryStatuses.ReadytoTest ||
      this.spacerStatus === SlurryStatuses.SentToLabAgain;
  }

  get showMudMeasurement(): boolean {
    //show mud measurement if compatibility or sst tests are selected
    return this.availableTests.some(t => t.isSelected && (t.type === 'compatibility' || t.type === 'ssst'));
  }
}
